<script setup lang="ts">
import image from '@/assets/main/event.webp'
import Counter from '@/components/shared/admin/orders/New/Counter.vue'
import { Button } from '@/components/ui/button'
import { Card, CardHeader, CardTitle } from '@/components/ui/card'
import CardContent from '@/components/ui/card/CardContent.vue'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Separator } from '@/components/ui/separator'
import { Stepper, StepperIndicator, StepperItem, StepperTitle } from '@/components/ui/stepper'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Textarea } from '@/components/ui/textarea'
import { useOrder } from '@/composables/admin/useOrder'
import LightLayout from '@/layouts/LightLayout.vue'
import { Head } from '@inertiajs/vue3'
import { ArrowLeftCircle, Check, Ticket } from 'lucide-vue-next'

interface TicketProps {
    id: number
    category: 'full_pass' | 'full_content' | 'digital'
    date: string
}

interface DataProps {
    start: string
    end: string
    description: string
    tickets: TicketProps[]
    order_groups: string[]
}

const props = defineProps<{
    data: DataProps
}>()

const { currentStep, steps, form, tickets, handleSubmit, handleTicket, findTicketById } = useOrder(props.data)
</script>

<template>
    <Head :title="$t('Register order')" />

    <LightLayout>
        <Stepper v-model="currentStep" class="font-inter container mb-12 flex w-full flex-col items-start gap-2 overflow-x-auto lg:flex-row">
            <StepperItem v-for="step in steps" :key="step.step" v-slot="{ state }" class="flex w-fit first:pl-0 lg:px-2" :step="step.step">
                <StepperIndicator
                    class="group-data-[state=completed]:bg-primary group-data-[state=active]:bg-tranparent group-data-[state=active]:border-primary group-data-[state=active]:text-primary h-[40px] w-[40px] group-data-[state=active]:border group-data-[state=active]:border-dashed group-data-[state=completed]:text-white group-data-[state=inactive]:border"
                >
                    <Check v-if="state === 'completed'" />
                    <span v-if="state != 'completed'">{{ String(step.step).padStart(2, '0') }}</span>
                </StepperIndicator>
                <StepperTitle
                    class="group-data-[state=active]:text-primary text-sm font-medium text-gray-900 group-data-[state=inactive]:text-slate-400"
                >
                    {{ $t(step.title) }}
                </StepperTitle>
            </StepperItem>
        </Stepper>
        <div class="container grid items-start gap-8 lg:grid-cols-[1fr_384px]">
            <div>
                <h2 class="mb-4 flex items-center gap-2 font-sans text-4xl font-semibold">
                    <button @click="currentStep--" aria-label="Back" type="button" v-if="currentStep > 1" class="cursor-pointer">
                        <ArrowLeftCircle stroke-width="1.2" />
                    </button>
                    {{ $t('Tickets') }}
                </h2>
                <form @submit.prevent="handleSubmit" id="form">
                    <Card v-if="currentStep === 1">
                        <CardContent class="p-0">
                            <div class="flex gap-4 p-6 lg:gap-6">
                                <div>
                                    <img :src="image" alt="" width="167" height="167" class="rounded-2xl" />
                                </div>
                                <div class="flex-1 font-sans">
                                    <h2>
                                        <strong>
                                            {{
                                                new Intl.DateTimeFormat('pt-BR', {
                                                    day: '2-digit',
                                                }).format(new Date(data.start))
                                            }}
                                        </strong>

                                        e

                                        <strong>
                                            {{
                                                new Intl.DateTimeFormat('pt-BR', {
                                                    day: '2-digit',
                                                }).format(new Date(data.end))
                                            }}
                                        </strong>

                                        <span>
                                            {{ ' ' }}
                                            {{
                                                new Intl.DateTimeFormat('pt-BR', {
                                                    month: 'long',
                                                    year: 'numeric',
                                                }).format(new Date(data.start))
                                            }}
                                        </span>
                                    </h2>

                                    <h1 class="mt-4 text-2xl font-bold">{{ $t(data.description) }}</h1>

                                    <p class="mt-2 text-sm text-slate-600 lg:text-base/relaxed">
                                        {{
                                            $t(
                                                'Join the event that is transforming Brazilian logistics and shaping the future of the supply chain. At the ILOS Forum, you will find leading executives, players and academics discussing global trends, challenges and innovations that impact the sector.',
                                                { event_description: $t(data.description) },
                                            )
                                        }}
                                    </p>
                                </div>
                            </div>

                            <div class="font-inter mt-16 grid px-6 pb-6">
                                <div>
                                    <Table class="w-max overflow-x-auto sm:w-full">
                                        <TableHeader>
                                            <TableRow>
                                                <TableHead class="w-[300px] py-6 pl-0 text-base font-semibold text-slate-800">
                                                    {{ $t('Tickets') }}
                                                </TableHead>
                                                <TableHead class="text-base font-semibold text-slate-800">
                                                    {{ $t('Quantity') }}
                                                </TableHead>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            <TableRow v-for="ticket in tickets" :key="ticket.id" class="border-transparent">
                                                <TableCell class="flex gap-4 p-4 pl-0">
                                                    <div class="flex items-center gap-6 text-lg font-semibold">
                                                        <div class="flex size-11 items-center justify-center rounded-full bg-cyan-50 text-cyan-700">
                                                            <Ticket />
                                                        </div>
                                                        {{ $t(ticket.category) }}
                                                    </div>
                                                </TableCell>
                                                <TableCell>
                                                    <Counter
                                                        @handleTicket="handleTicket"
                                                        :ticket="ticket"
                                                        :counter="form.items_attributes.find((item) => item.ticket_id === ticket.id)?.quantity"
                                                    />
                                                </TableCell>
                                            </TableRow>
                                        </TableBody>
                                    </Table>
                                    <p v-if="form.errors.items_attributes" class="text-destructive text-xs">{{ form.errors.items_attributes }}</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    <div class="grid gap-4 lg:grid-cols-2" v-if="currentStep === 2">
                        <div>
                            <Label required for="email">E-mail para envio</Label>
                            <Input v-model="form.user_email" type="text" id="email" placeholder="email@email.com" />
                            <p v-if="form.errors.user_email" class="text-destructive text-xs">{{ $t(form.errors.user_email) }}</p>
                        </div>

                        <div>
                            <Label required for="group">Grupo de Ingresso</Label>
                            <Select v-model="form.group" id="group">
                                <SelectTrigger class="w-full">
                                    <SelectValue placeholder="Selecione um grupo" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem
                                        v-for="group in [
                                            'purchased_ticket',
                                            'speaker',
                                            'sponsor',
                                            'summit_access',
                                            'press',
                                            'production',
                                            'exhibitor',
                                            'courtesy',
                                            'staff',
                                            'supporter',
                                            'guest',
                                        ]"
                                        :key="`${group}-groups`"
                                        :value="group"
                                    >
                                        {{ $t(`groups.${group}`) }}
                                    </SelectItem>
                                </SelectContent>
                            </Select>
                            <p v-if="form.errors.group" class="text-destructive text-xs">{{ form.errors.group }}</p>
                        </div>

                        <Card class="col-span-full flex-1 overflow-hidden">
                            <CardHeader class="bg-slate-50">
                                <CardTitle>Ingresso com isenção de valor</CardTitle>
                            </CardHeader>
                            <CardContent class="pt-6 pb-14">
                                <div>
                                    <Label for="reason">Motivo para isenção do valor</Label>
                                    <Textarea
                                        v-model="form.reason_free_order"
                                        id="reason"
                                        class="mt-4 resize-none"
                                        placeholder="Informe o motivo da isenção do valor"
                                    />
                                    <p v-if="form.errors.reason_free_order" class="text-destructive text-xs">{{ form.errors.reason_free_order }}</p>
                                </div>
                            </CardContent>
                        </Card>
                    </div>
                </form>
            </div>
            <Card class="bg-slate-50">
                <CardContent class="p-6">
                    <h3 class="text-lg font-medium" v-if="form.items_attributes.length">Resumo do pedido</h3>
                    <h3 class="text-lg font-medium" v-if="!form.items_attributes.length">Nenhum ingresso selecionado</h3>
                    <div class="mt-4">
                        <div class="space-y-4">
                            <div v-for="ticket in form.items_attributes" :key="ticket.ticket_id" class="flex gap-3">
                                <div class="flex size-11 items-center justify-center rounded-full bg-cyan-50 text-cyan-700">
                                    <Ticket />
                                </div>
                                <div class="flex-1">
                                    <h3>Tipo de ingresso</h3>
                                    <div class="flex items-center justify-between gap-2">
                                        <strong>{{ $t(findTicketById(ticket.ticket_id)!.category) }}</strong>

                                        <span class="text-sm text-slate-600"> {{ ticket.quantity }}x </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <Separator class="my-6" />

                    <div class="flex justify-between">
                        <strong class="text-lg font-semibold">Total Quantidade:</strong>

                        <span class="text-lg font-semibold"> {{ form.items_attributes.reduce((acc, item) => acc + item.quantity, 0) }}x </span>
                    </div>
                    <Button type="submit" form="form" class="mt-4 w-full" :disabled="form.processing || form.items_attributes.length === 0">
                        <template v-if="currentStep === 1"> Próximo </template>
                        <template v-else-if="currentStep === 2">
                            {{ $t('Register order') }}
                        </template>
                    </Button>
                </CardContent>
            </Card>
        </div>
    </LightLayout>
</template>
